import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/contemplation-silhouette.jpg";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { trackCta } from "@/lib/trackCta";

const VIDEO_ID = "DHlgKeP3yvo";

const getSessionId = (): string => {
  try {
    let id = sessionStorage.getItem("analyticsSessionId");
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem("analyticsSessionId", id);
    }
    return id;
  } catch {
    return "anonymous";
  }
};

export const Hero = () => {
  const [scrollY, setScrollY] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);
  const openedAtRef = useRef<number | null>(null);

  const handleOpenVideo = async () => {
    try {
      sessionStorage.removeItem("heroVideoClosed");
    } catch {
      // ignore storage errors (private mode, etc.)
    }
    setVideoOpen(true);
    openedAtRef.current = Date.now();
    trackCta("home_hero_watch_message", `youtube:${VIDEO_ID}`, "Watch the Message");
    void supabase.functions.invoke("track-event", {
      body: {
        kind: "video",
        event_type: "open",
        video_id: VIDEO_ID,
        source: "hero",
        session_id: getSessionId(),
      },
    });
  };

  const handleVideoOpenChange = (open: boolean) => {
    if (!open) {
      try {
        sessionStorage.setItem("heroVideoClosed", "1");
      } catch {
        // ignore
      }
      const startedAt = openedAtRef.current;
      const watchSeconds = startedAt
        ? Math.max(0, Math.round((Date.now() - startedAt) / 1000))
        : null;
      openedAtRef.current = null;
      void supabase.functions.invoke("track-event", {
        body: {
          kind: "video",
          event_type: "close",
          video_id: VIDEO_ID,
          source: "hero",
          session_id: getSessionId(),
          watch_seconds: watchSeconds,
        },
      });
    }
    setVideoOpen(open);
  };
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const parallaxSpeed = 0.5;
  const imageTransform = `translateY(${scrollY * parallaxSpeed}px)`;
  const overlayOpacity = Math.min(1, 0.8 + scrollY / 500);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0" style={{ transform: imageTransform, transition: "transform 0.1s ease-out" }}>
        <img 
          src={heroImage} 
          alt="Rob Lasa" 
          width="1920"
          height="1080"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover scale-110" 
          style={{ willChange: "transform" }} 
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/80 to-secondary/90" style={{ opacity: overlayOpacity, transition: "opacity 0.1s ease-out" }} />
      </div>

      <div className="absolute inset-0 bg-gradient-glow z-0 animate-glow" />

      <div className="relative z-10 container mx-auto px-4 text-center pt-20 md:pt-0">
        <div className="max-w-4xl mx-auto space-y-12">
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-tight px-4 sm:px-0 animate-fade-in mt-8 md:mt-8" style={{ animationDelay: "0.2s", animationFillMode: "both" }}>
            <span className="block font-serif font-semibold">I'm Not Defective.</span>
            <span className="block font-semibold font-serif">I Was Just <span className="text-accent drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">Buried</span>.</span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-primary-foreground/90 font-light max-w-3xl mx-auto leading-relaxed px-4 sm:px-6 animate-fade-in mt-6" style={{ animationDelay: "0.3s", animationFillMode: "both" }}>
            A man in the middle of his life, documenting what happens when you stop performing.
            <br />
            For my sons. For any man who sees himself in it.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 pt-4 px-4 sm:px-0 animate-fade-in" style={{ animationDelay: "0.4s", animationFillMode: "both" }}>
            <Button size="lg" asChild className="w-full sm:w-auto bg-accent text-white hover:bg-accent/90 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 shadow-glow transition-all hover:scale-105">
              <Link to="/about" onClick={() => trackCta("home_hero_start", "/about", "Start Here")}>
                Start Here
                <ArrowRight className="ml-2 h-4 sm:h-5 w-4 sm:w-5" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleOpenVideo}
              className="w-full sm:w-auto bg-transparent border-white/70 text-white hover:bg-white hover:text-primary text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 transition-all hover:scale-105 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Play className="mr-2 h-4 sm:h-5 w-4 sm:w-5" />
              Watch the Message
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={videoOpen} onOpenChange={handleVideoOpenChange}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-background border-border">
          <DialogTitle className="sr-only">Watch the Message</DialogTitle>
          <div className="relative aspect-video w-full">
            {videoOpen && (
              <iframe
                key={captionsOn ? "cc-on" : "cc-off"}
                src={`https://www.youtube.com/embed/DHlgKeP3yvo?autoplay=1&rel=0&cc_load_policy=${captionsOn ? 1 : 0}${captionsOn ? "&cc_lang_pref=en&hl=en" : ""}`}
                title="Rob Lasa — The Message"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
          <div className="flex items-center justify-end gap-3 px-4 py-3 bg-background border-t border-border">
            <Label htmlFor="captions-toggle" className="text-sm text-foreground cursor-pointer">
              Captions
            </Label>
            <Switch
              id="captions-toggle"
              checked={captionsOn}
              onCheckedChange={setCaptionsOn}
              aria-label="Toggle captions"
            />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
